<template>
  <LayoutModal ref="modal">
    <LayoutModalContent class="modal-content">
      <CommonReturn text="Retour" @click="modal?.close()" />
      <template v-if="nps_store.detail">
        <div class="categories">
          <CommonChip
            v-for="category of nps_store.detail.serviceCategories"
            :key="category"
            size="large"
            transparent
            color="dark"
          >
            {{ category }}
          </CommonChip>
        </div>
        <div class="name">
          <h2>{{ nps_store.detail.title }}</h2>
          <p v-if="nps_store.detail.consultantName">
            Intervenant : {{ nps_store.detail.consultantName }}
          </p>
        </div>
        <div class="status">
          <CommonChip v-if="nps_store.detail.customer" size="large" filled color="dark">
            {{ nps_store.detail.customer }}
          </CommonChip>
          <CommonChip v-if="nps_store.detail.backer" size="large" color="dark">
            {{ nps_store.detail.backer }}
          </CommonChip>
        </div>
        <div class="informations">
          <LabelDate :size="v_mobile ? 'small' : 'medium'" :date="nps_store.detail.date" />
          <LabelTime
            v-if="nps_store.detail.startTime && nps_store.detail.endTime"
            :size="v_mobile ? 'small' : 'medium'"
            :start="nps_store.detail.startTime"
            :end="nps_store.detail.endTime"
          />
          <LabelLocalization
            v-if="nps_store.detail.address || nps_store.detail.city || nps_store.detail.postcode"
            :size="v_mobile ? 'small' : 'medium'"
            :localization="
              makeAddressString(
                nps_store.detail.address ?? undefined,
                nps_store.detail.postcode ?? undefined,
                nps_store.detail.city ?? undefined
              )
            "
          />
        </div>
        <CommonParagraph title="Commentaires client">
          <div class="comment">
            <p v-if="nps_store.detail.scoreLabel" class="comment-label">
              {{ nps_store.detail.scoreLabel }}
            </p>
            <CommonStatChip size="large" transparent color="success">
              {{ formatAverageScore(nps_store.detail.score) }}
            </CommonStatChip>
          </div>
          <div v-for="comment in nps_store.detail.comments" :key="comment.label" class="comment">
            <p class="comment-label">{{ comment.label }}</p>
            <p>{{ comment.value }}</p>
          </div>
          <p v-if="nps_store.detail.comments.length === 0" class="no-comment">Aucun commentaire.</p>
        </CommonParagraph>
      </template>
    </LayoutModalContent>
  </LayoutModal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import CommonChip from '@/components/common/CommonChip.vue'
import CommonParagraph from '@/components/common/CommonParagraph.vue'
import CommonReturn from '@/components/common/CommonReturn.vue'
import CommonStatChip from '@/components/common/CommonStatChip.vue'
import LayoutModal from '@/components/layout/LayoutModal.vue'
import LayoutModalContent from '@/components/layout/LayoutModalContent.vue'
import LabelDate from '../label/LabelDate.vue'
import LabelTime from '../label/LabelTime.vue'
import LabelLocalization from '../label/LabelLocalization.vue'
import { makeAddressString } from '@/services/service_date'
import { formatAverageScore } from '@/services/service_nps'
import { useNpsStore } from '@/stores/gae/store_nps'

const nps_store = useNpsStore()
const modal = ref<InstanceType<typeof LayoutModal>>()
const breakpoint = useBreakpoints(breakpointsTailwind)
const v_mobile = breakpoint.smallerOrEqual('sm')

function open() {
  modal.value?.open()
}

defineExpose({ open })
</script>

<style scoped lang="scss">
.modal-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 46px;
  margin-bottom: 120px;
}

.categories,
.status {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
}

.name h2 {
  color: var(--vc-dark);
  font-size: 28px;
  font-weight: 600;
  font-family: 'Jost';
}

.name p {
  color: rgba(var(--vcrgb-dark), 0.75);
  font-style: italic;
}

.informations {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 20px;
}

.comment {
  margin-bottom: 20px;
}

.comment-label {
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 15px;
}

.comment p,
.no-comment {
  color: rgba(var(--vcrgb-dark), 0.75);
}
</style>
